/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  searchPlayers,
  searchTeams,
  fetchTeamRoster,
  fetchPlayerSeasons,
  fetchPlayerParameters,
  fetchAggregatedEvents
} from './eventsExplorerService';

export const MAX_PLAYERS = 100;

/**
 * Generate a visually distinct color for a given index using HSL color space.
 * Uses golden angle distribution for optimal hue spread, with varying
 * saturation and lightness to ensure colors remain distinguishable.
 */
export function generateColor(index) {
  // Golden angle in degrees for optimal hue distribution
  const goldenAngle = 137.508;
  const hue = (index * goldenAngle) % 360;

  // Vary saturation and lightness in cycles to add variety
  const saturationCycle = [75, 85, 65, 90, 70];
  const lightnessCycle = [50, 45, 55, 40, 60];

  const saturation = saturationCycle[index % saturationCycle.length];
  const lightness = lightnessCycle[index % lightnessCycle.length];

  return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
}

function assignPlayerColor(existingPlayers) {
  // Find the next available color index
  const usedColors = new Set(existingPlayers.map(p => p.color));

  for (let i = 0; i < MAX_PLAYERS; i++) {
    const color = generateColor(i);
    if (!usedColors.has(color)) {
      return color;
    }
  }

  // Fallback: generate based on current count
  return generateColor(existingPlayers.length);
}

const initialState = {
  selectedPlayers: [],

  globalParameters: {
    checkedIds: [],
    checkedActions: []
  },

  parameters: [],

  events: [],

  // Phase 12: while non-null the pitch shows sequence chains instead of the
  // normal player view. Shape: { label, team, competition, season, total,
  // focusedIndex, sequences: [{index, match, event_count, players}] }.
  // Cleared by any normal pitch redraw (setEvents/clearEvents/fetch).
  sequenceOverlay: null,

  // Jam 17: while non-null the pitch shows a loaded playlist's events (plain
  // event dots, no chains) instead of the normal player view. Shape:
  // { title, playlist_id, total }. Cleared by any normal pitch redraw, and
  // mutually exclusive with sequenceOverlay (only one overlay shows at a time).
  playlistOverlay: null,

  search: {
    mode: 'player',
    query: '',
    results: [],
    selectedTeam: null,
    teamRoster: []
  },

  loading: {
    search: false,
    roster: false,
    playerSeasons: false,
    events: false,
    parameters: false
  },

  errors: {
    search: null,
    roster: null,
    events: null,
    parameters: null
  },

  ui: {
    isConfigModalOpen: false,
    configModalPlayerId: null,
    isTeamConfigModalOpen: false,
    teamConfigModalTeamId: null,
    fieldSettings: {
      zones: 0,
      drawingMode: false,
      deselectMode: false, // click-to-deselect: clicking a dot hides it (Jam 5)
      drawnShapes: [],
      showDirections: false,
      timeRange: [0, 90],
      colorGrouping: 'player', // 'player' or 'team'
      selectedZoneEventIds: [], // event IDs from clicked zone rectangles
      excludedEventIds: [], // events hidden via the AI's exclude_events tool
      eventColorOverrides: {}, // { [eventId]: colorString } custom per-event colours (AI set_event_color)
      gameState: null, // 'winning' | 'losing' | 'drawing' | null (off)
      gameStateEventIds: null, // include-list computed by set_game_state_filter
      zoneDisplayFilter: null // 'inside_box' | 'outside_box' | null — show only events in that pitch zone (set_zone_display_filter)
    },
    isAutoConfigModalOpen: false,
    isAIHelperOpen: false,
    voiceMode: (() => { try { const v = localStorage.getItem('ai_voice_enabled'); return v === null ? true : v === 'true'; } catch { return true; } })(),
    aiHelper: {
      messages: [],
      isProcessing: false,
      pendingAction: null,
      currentStatus: null
    }
  }
};

export const eventsExplorerSlice = createSlice({
  name: 'eventsExplorer',
  initialState,
  reducers: {
    addPlayer: (state, action) => {
      const player = action.payload;
      if (state.selectedPlayers.length >= MAX_PLAYERS) return;
      if (state.selectedPlayers.find(p => p.id === player.id)) return;

      const color = assignPlayerColor(state.selectedPlayers);
      state.selectedPlayers.push({
        ...player,
        color,
        isActive: true,
        config: {
          useGlobalParams: true,
          seasonId: null,
          competitionId: null,
          matchIds: [],
          parameterIds: [],
          availableSeasons: null,
          teamConfiguredBy: null
        }
      });
    },

    removePlayer: (state, action) => {
      const playerId = action.payload;
      state.selectedPlayers = state.selectedPlayers.filter(p => p.id !== playerId);
    },

    togglePlayerActive: (state, action) => {
      const { playerId, active } = action.payload;
      const player = state.selectedPlayers.find(p => p.id === playerId);
      if (player) {
        player.isActive = active ?? !player.isActive;
      }
    },

    updatePlayerConfig: (state, action) => {
      const { playerId, config } = action.payload;
      const player = state.selectedPlayers.find(p => p.id === playerId);
      if (player) {
        player.config = { ...player.config, ...config };
      }
    },

    toggleGlobalParameter: (state, action) => {
      const param = action.payload;
      const index = state.globalParameters.checkedIds.indexOf(param.id);
      if (index > -1) {
        state.globalParameters.checkedIds.splice(index, 1);
        state.globalParameters.checkedActions = state.globalParameters.checkedActions
          .filter(a => a.id !== param.id);
      } else {
        state.globalParameters.checkedIds.push(param.id);
        state.globalParameters.checkedActions.push(param);
      }
    },

    ensureGlobalParameterSelected: (state, action) => {
      const param = action.payload;
      if (!state.globalParameters.checkedIds.includes(param.id)) {
        state.globalParameters.checkedIds.push(param.id);
        state.globalParameters.checkedActions.push(param);
      }
    },

    clearGlobalParameters: (state) => {
      state.globalParameters.checkedIds = [];
      state.globalParameters.checkedActions = [];
    },

    setGlobalParameters: (state, action) => {
      state.globalParameters.checkedIds = action.payload.map(p => p.id);
      state.globalParameters.checkedActions = action.payload;
    },

    setSearchMode: (state, action) => {
      state.search.mode = action.payload;
      state.search.query = '';
      state.search.results = [];
      state.search.selectedTeam = null;
      state.search.teamRoster = [];
    },

    setSearchQuery: (state, action) => {
      state.search.query = action.payload;
    },

    clearSearch: (state) => {
      state.search.query = '';
      state.search.results = [];
      state.search.selectedTeam = null;
      state.search.teamRoster = [];
    },

    selectTeamForRoster: (state, action) => {
      state.search.selectedTeam = action.payload;
    },

    clearTeamRoster: (state) => {
      state.search.selectedTeam = null;
      state.search.teamRoster = [];
    },

    openConfigModal: (state, action) => {
      state.ui.isConfigModalOpen = true;
      state.ui.configModalPlayerId = action.payload;
    },

    closeConfigModal: (state) => {
      state.ui.isConfigModalOpen = false;
      state.ui.configModalPlayerId = null;
    },

    openTeamConfigModal: (state, action) => {
      state.ui.isTeamConfigModalOpen = true;
      state.ui.teamConfigModalTeamId = action.payload;
    },

    closeTeamConfigModal: (state) => {
      state.ui.isTeamConfigModalOpen = false;
      state.ui.teamConfigModalTeamId = null;
    },

    updateTeamPlayersConfig: (state, action) => {
      const { teamId, config } = action.payload;
      state.selectedPlayers.forEach(player => {
        if (player.team?.id === teamId) {
          player.config = {
            ...player.config,
            ...config,
            teamConfiguredBy: teamId
          };
        }
      });
    },

    clearTeamConfiguredBy: (state, action) => {
      const playerId = action.payload;
      const player = state.selectedPlayers.find(p => p.id === playerId);
      if (player) {
        player.config.teamConfiguredBy = null;
      }
    },

    openAutoConfigModal: (state) => {
      state.ui.isAutoConfigModalOpen = true;
    },

    closeAutoConfigModal: (state) => {
      state.ui.isAutoConfigModalOpen = false;
    },

    updateFieldSettings: (state, action) => {
      state.ui.fieldSettings = { ...state.ui.fieldSettings, ...action.payload };
    },

    resetFieldSettings: (state) => {
      state.ui.fieldSettings = initialState.ui.fieldSettings;
    },

    setEvents: (state, action) => {
      state.events = action.payload;
      // Any plain events write exits overlay mode; the sequence/playlist tools
      // re-dispatch their own overlay right after their setEvents.
      state.sequenceOverlay = null;
      state.playlistOverlay = null;
    },

    clearEvents: (state) => {
      state.events = [];
      state.sequenceOverlay = null;
      state.playlistOverlay = null;
    },

    setSequenceOverlay: (state, action) => {
      state.sequenceOverlay = action.payload;
      // The two overlays are mutually exclusive — entering sequence mode
      // leaves playlist mode.
      state.playlistOverlay = null;
    },

    clearSequenceOverlay: (state) => {
      state.sequenceOverlay = null;
    },

    setPlaylistOverlay: (state, action) => {
      state.playlistOverlay = action.payload;
      // Mutually exclusive with sequence mode.
      state.sequenceOverlay = null;
    },

    clearPlaylistOverlay: (state) => {
      state.playlistOverlay = null;
    },

    resetAll: (state) => {
      const wasAIHelperOpen = state.ui.isAIHelperOpen;
      return { ...initialState, ui: { ...initialState.ui, isAIHelperOpen: wasAIHelperOpen } };
    },

    // AI Helper actions
    toggleAIHelper: (state) => {
      state.ui.isAIHelperOpen = !state.ui.isAIHelperOpen;
    },

    setAIHelperOpen: (state, action) => {
      state.ui.isAIHelperOpen = action.payload;
    },

    addAIMessage: (state, action) => {
      state.ui.aiHelper.messages.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        ...action.payload
      });
    },

    setAIProcessing: (state, action) => {
      state.ui.aiHelper.isProcessing = action.payload;
    },

    setAIPendingAction: (state, action) => {
      state.ui.aiHelper.pendingAction = action.payload;
    },

    clearAIPendingAction: (state) => {
      state.ui.aiHelper.pendingAction = null;
    },

    clearAIMessages: (state) => {
      state.ui.aiHelper.messages = [];
    },

    // Replace the whole transcript (chat-history hydration / session switch)
    setAIMessages: (state, action) => {
      state.ui.aiHelper.messages = (action.payload || []).map((message, index) => ({
        id: message.id || `restored-${index}`,
        timestamp: message.timestamp || message.created_at || new Date().toISOString(),
        ...message
      }));
    },

    removeLastAIMessage: (state) => {
      state.ui.aiHelper.messages.pop();
    },

    updateLastAIMessage: (state, action) => {
      const lastIndex = state.ui.aiHelper.messages.length - 1;
      if (lastIndex >= 0) {
        state.ui.aiHelper.messages[lastIndex] = {
          ...state.ui.aiHelper.messages[lastIndex],
          ...action.payload,
          isLoading: false
        };
      }
    },

    setAICurrentStatus: (state, action) => {
      state.ui.aiHelper.currentStatus = action.payload;
    },

    setVoiceMode: (state, action) => {
      state.ui.voiceMode = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(searchPlayers.pending, (state) => {
        state.loading.search = true;
        state.errors.search = null;
      })
      .addCase(searchPlayers.fulfilled, (state, action) => {
        state.loading.search = false;
        state.search.results = action.payload.results || [];
      })
      .addCase(searchPlayers.rejected, (state, action) => {
        state.loading.search = false;
        state.errors.search = action.payload;
      })

      .addCase(searchTeams.pending, (state) => {
        state.loading.search = true;
        state.errors.search = null;
      })
      .addCase(searchTeams.fulfilled, (state, action) => {
        state.loading.search = false;
        state.search.results = action.payload.results || [];
      })
      .addCase(searchTeams.rejected, (state, action) => {
        state.loading.search = false;
        state.errors.search = action.payload;
      })

      .addCase(fetchTeamRoster.pending, (state) => {
        state.loading.roster = true;
        state.errors.roster = null;
      })
      .addCase(fetchTeamRoster.fulfilled, (state, action) => {
        state.loading.roster = false;
        state.search.teamRoster = action.payload;
      })
      .addCase(fetchTeamRoster.rejected, (state, action) => {
        state.loading.roster = false;
        state.errors.roster = action.payload;
      })

      .addCase(fetchPlayerSeasons.pending, (state) => {
        state.loading.playerSeasons = true;
      })
      .addCase(fetchPlayerSeasons.fulfilled, (state, action) => {
        state.loading.playerSeasons = false;
      })
      .addCase(fetchPlayerSeasons.rejected, (state) => {
        state.loading.playerSeasons = false;
      })

      .addCase(fetchPlayerParameters.pending, (state) => {
        state.loading.parameters = true;
        state.errors.parameters = null;
      })
      .addCase(fetchPlayerParameters.fulfilled, (state, action) => {
        state.loading.parameters = false;
        state.parameters = action.payload;
      })
      .addCase(fetchPlayerParameters.rejected, (state, action) => {
        state.loading.parameters = false;
        state.errors.parameters = action.payload;
      })

      .addCase(fetchAggregatedEvents.pending, (state) => {
        state.loading.events = true;
        state.errors.events = null;
      })
      .addCase(fetchAggregatedEvents.fulfilled, (state, action) => {
        state.loading.events = false;
        state.events = action.payload;
        state.sequenceOverlay = null;
        state.playlistOverlay = null;
      })
      .addCase(fetchAggregatedEvents.rejected, (state, action) => {
        state.loading.events = false;
        state.errors.events = action.payload;
      });
  }
});

export const { actions: eventsExplorerActions } = eventsExplorerSlice;
export const { reducer: eventsExplorerReducer } = eventsExplorerSlice;
